import { getFilterIcon } from "@/components/inputs/DynamicTagsInputs/FilterInput/comps/DocHierarchyFilter/utils/getFilterIcon";
import { getProperTitleFromContentTypeId } from "@/components/analysisModules/AnnotationExplorer/utils/getProperTitleFromContentTypeId";
import { clusterBlocksByOrGroup } from "@/components/discovery/FilterModule/utils/clusterBlocksByOrGroup";

/**
 * Collects all filter IDs from a docHierarchyType config.
 */
const getAllDocFilterIds = (docConfig) => {
  if (!docConfig?.source) return new Set();
  const {
    commonFilters = [],
    sharedAcrossMultiple = [],
    commonDocumentFilters = [],
    perDataset = [],
  } = docConfig.source;
  return new Set([
    ...commonFilters.map((f) => f.filterId),
    ...sharedAcrossMultiple.flatMap((g) =>
      (g.filters || []).map((f) => f.filterId)
    ),
    ...commonDocumentFilters.map((f) => f.filterId),
    ...perDataset.flatMap((d) => [
      ...(d.filters || []).map((f) => f.filterId),
      ...(d.rollups || []).map((f) => f.filterId),
      ...(d.docFieldFilter ? [d.docFieldFilter.filterId] : []),
    ]),
  ]);
};

/**
 * Detect if a filter belongs to the doc hierarchy.
 */
export const isDocFilter = (filter, docHierarchyConfig) => {
  return getAllDocFilterIds(docHierarchyConfig).has(filter.filterId);
};

/**
 * Finds a filter config by filterId within the docHierarchy structure.
 * Returns the config along with the dataset display name.
 */
const findFilterConfig = (filterId, docConfig) => {
  if (!docConfig?.source) return null;
  const {
    commonFilters = [],
    sharedAcrossMultiple = [],
    commonDocumentFilters = [],
    perDataset = [],
  } = docConfig.source;

  // Check common filters
  const common = commonFilters.find((f) => f.filterId === filterId);
  if (common) return { ...common, datasetDisplay: "Shared across all" };

  // Check shared-across-multiple filters
  for (const group of sharedAcrossMultiple) {
    const found = (group.filters || []).find((f) => f.filterId === filterId);
    if (found) return { ...found, datasetDisplay: group.contentTypes?.length > 1 ? `${group.contentTypes.length} Datasets` : group.display, contentTypes: group.contentTypes };
  }

  // Check common document filters (Author, Published Date)
  const commonDoc = commonDocumentFilters.find((f) => f.filterId === filterId);
  if (commonDoc) return { ...commonDoc, datasetDisplay: "Metadata" };

  // Check per-dataset filters
  for (const dataset of perDataset) {
    const found =
      (dataset.filters || []).find((f) => f.filterId === filterId) ||
      (dataset.rollups || []).find((f) => f.filterId === filterId) ||
      (dataset.docFieldFilter?.filterId === filterId ? dataset.docFieldFilter : null);
    // Carry the dataset's own contentType so downstream aggregations (e.g. the
    // number-range histogram/bounds fetch) query the right collection rather
    // than falling back to the page-level contentTypes. Mirrors the Add Filter
    // cascade, which passes contentTypes={[dataset.contentType]}.
    if (found) return {
      ...found,
      datasetDisplay: dataset.display,
      ...(dataset.contentType ? { contentTypes: [dataset.contentType] } : {}),
    };
  }

  return null;
};

/**
 * Derives the breadcrumb for a doc filter based on its position in the config hierarchy.
 */
const getDocBreadcrumb = (filterId, docHierarchyConfig, resolvedConfig, datasetCount) => {
  const isCommon = (docHierarchyConfig.source.commonFilters || []).some(
    (f) => f.filterId === filterId
  );
  const isSharedMulti = (
    docHierarchyConfig.source.sharedAcrossMultiple || []
  ).some((g) =>
    (g.filters || []).some((f) => f.filterId === filterId)
  );
  const isMetadata = (
    docHierarchyConfig.source.commonDocumentFilters || []
  ).some((f) => f.filterId === filterId);

  // With a single dataset, "common to all datasets" is trivially true for every
  // filter, so "Shared across all" reads as nonsense. Render such filters flat.
  if (isCommon && datasetCount <= 1) return [];

  return isCommon
    ? ["Shared across all"]
    : isSharedMulti
    ? [resolvedConfig.datasetDisplay]
    : isMetadata
    ? []
    : [resolvedConfig.datasetDisplay];
};

/**
 * Groups active document filters into block descriptors for rendering.
 * When savedConfigs are provided, generates empty blocks (values: []) for saved
 * configs that have no active values. Doc breadcrumbs are derived from the config
 * hierarchy structure, so no _savedGroups are needed.
 *
 * @param {Array} activeFilters - Currently active filters array
 * @param {Object} docHierarchyConfig - The docHierarchyType config
 * @param {Array} [savedConfigs] - Saved filter configs for empty block generation
 * @param {number} [datasetCount] - Number of selected datasets; drives "Shared across all" suppression
 * @returns {Array<BlockDescriptor>}
 */
export const groupDocFilters = (activeFilters, docHierarchyConfig, savedConfigs = [], datasetCount = Infinity) => {
  if (!docHierarchyConfig?.source) return [];

  const docFilterIds = getAllDocFilterIds(docHierarchyConfig);
  const blocks = [];

  for (const filter of activeFilters) {
    if (!docFilterIds.has(filter.filterId)) continue;
    // Array-based filters (multiselect, dateRange) use .length;
    // object-based filters (numberRange: {min, max}) have no .length
    const vals = filter.values;
    const hasValues = Array.isArray(vals)
      ? vals.length > 0
      : vals != null && typeof vals === "object" && Object.keys(vals).length > 0;
    if (!hasValues) continue;

    const config = findFilterConfig(filter.filterId, docHierarchyConfig);
    if (!config) continue;

    const { icon: levelIcon, iconWeight: levelIconWeight } = getFilterIcon(config);
    blocks.push({
      key: filter.filterId,
      filterId: filter.filterId,
      // OR-group identity (filters that combine with OR share this), read straight
      // off the value the FE change handler stamped onto the activeFilter — the
      // single source of truth. Deliberately NOT recomputed here: deriving it a
      // second time would risk drifting from the stamped value.
      orGroupKey: filter.orGroupKey,
      level: "docFilter",
      levelIcon,
      ...(levelIconWeight ? { levelIconWeight } : {}),
      levelLabel: config.display,
      breadcrumb: getDocBreadcrumb(filter.filterId, docHierarchyConfig, config, datasetCount),
      ...(config.contentTypes?.length > 1
        ? {
            breadcrumbTooltip: config.contentTypes.map(getProperTitleFromContentTypeId).join("\n"),
            datasetNames: config.contentTypes.map(getProperTitleFromContentTypeId),
          }
        : {}),
      values: filter.values,
      filterConfig: config,
    });
  }

  // --- Empty blocks for saved configs without active values ---
  const coveredIds = new Set(blocks.map((b) => b.filterId));

  for (const config of savedConfigs) {
    if (coveredIds.has(config.filterId)) continue;
    if (!docFilterIds.has(config.filterId)) continue;

    const resolvedConfig = findFilterConfig(config.filterId, docHierarchyConfig);
    if (!resolvedConfig) continue;

    const { icon: levelIcon, iconWeight: levelIconWeight } =
      getFilterIcon(resolvedConfig);

    blocks.push({
      key: config.filterId,
      filterId: config.filterId,
      // No orGroupKey: an empty (no-value) saved block isn't OR'd into the query,
      // so it stays ungrouped — never clustered or given an "OR" connector.
      level: "docFilter",
      levelIcon,
      ...(levelIconWeight ? { levelIconWeight } : {}),
      levelLabel: resolvedConfig.display,
      breadcrumb: getDocBreadcrumb(config.filterId, docHierarchyConfig, resolvedConfig, datasetCount),
      ...(resolvedConfig.contentTypes?.length > 1
        ? {
            breadcrumbTooltip: resolvedConfig.contentTypes.map(getProperTitleFromContentTypeId).join("\n"),
            datasetNames: resolvedConfig.contentTypes.map(getProperTitleFromContentTypeId),
          }
        : {}),
      values: [],
      filterConfig: resolvedConfig,
    });
  }

  // Pull OR-siblings adjacent (+ annotate orGroupSize/orGroupPos for the OR
  // connector). Display-only; does not affect the query.
  return clusterBlocksByOrGroup(blocks);
};
